import React, { ChangeEvent, useRef } from "react";
import { useState } from "react";
import Alert from "@mui/material/Alert";

const CodeInput = ({
  length = 6,
  onComplete,
  disabled = false,
}: {
  length: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
}) => {
  const [code, setCode] = useState<Array<string>>(
    Array<string>(length).fill("")
  );
  const [error, setError] = useState<string>("");
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    if (disabled) return;
    setError("");
    const value = event.target.value.toUpperCase();
    if (!/^[A-Z0-9]$/.test(value)) {
      return;
    }
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    const next = inputs.current[index + 1];
    if (index < length - 1 && next) {
      next.focus();
    }

    if (newCode.every((char) => char)) {
      onComplete(newCode.join(""));
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (disabled) return;
    setError("");
    if (event.key !== "Backspace") {
      return;
    }

    const newCode = [...code];
    newCode[index] = "";
    setCode(newCode);
    const prev = inputs.current[index - 1];
    if (index > 0 && prev) {
      prev.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    const pastedData = event.clipboardData.getData("Text").toUpperCase();
    if (!new RegExp(`^[A-Z0-9]{${length}}$`).test(pastedData)) {
      setError(`Code must be exactly ${length} letters or numbers.`);
      event.preventDefault();
      return;
    }

    const newCode = pastedData.split("").slice(0, length);
    setCode(newCode);
    setError("");
    inputs.current[length - 1]?.focus();
    onComplete(newCode.join(""));
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "32px 24px",
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        margin: "40px auto",
        maxWidth: 400,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {error && (
        <Alert severity="error" style={{ marginBottom: 18, width: "100%" }}>
          {error}
        </Alert>
      )}
      <div
        style={{
          display: "flex",
          gap: "12px",
          justifyContent: "center",
          marginBottom: "18px",
        }}
      >
        {[...Array(length)].map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputs.current[index] = el)}
            type="text"
            value={code[index] || ""}
            maxLength={1}
            disabled={disabled}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={index === 0 ? handlePaste : undefined}
            style={{
              width: "48px",
              height: "48px",
              fontSize: "28px",
              textAlign: "center",
              textTransform: "uppercase",
              border: "2px solid #d1d5db",
              borderRadius: "8px",
              background: disabled ? "#f3f4f6" : "#f9fafb",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              outline: "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
              cursor: disabled ? "not-allowed" : "text",
            }}
            onFocus={(e) =>
              !disabled && (e.currentTarget.style.borderColor = "#6366f1")
            }
            onBlur={(e) => (e.currentTarget.style.borderColor = "#d1d5db")}
            onMouseOver={(e) =>
              !disabled &&
              (e.currentTarget.style.boxShadow =
                "0 2px 8px rgba(99,102,241,0.10)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)")
            }
          ></input>
        ))}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setCode(Array<string>(length).fill(""));
          setError("");
          inputs.current[0]?.focus();
        }}
        style={{
          padding: "10px 18px",
          fontSize: "16px",
          borderRadius: "8px",
          border: "1px solid #d1d5db",
          background: disabled ? "#e5e7eb" : "#f3f4f6",
          color: disabled ? "#9ca3af" : "#374151",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background 0.2s, border-color 0.2s",
        }}
        onMouseOver={(e) =>
          !disabled && (e.currentTarget.style.background = "#e0e7ef")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.background = disabled ? "#e5e7eb" : "#f3f4f6")
        }
      >
        Clear
      </button>
    </div>
  );
};

export default CodeInput;
